/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import Image from 'next/image'
import { Breadcrumbs, Text, FlightIcon } from '@hashicorp/mds-react/components'
import s from './style.module.css'
import Link from 'next/link'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'

export interface IAuthorData {
	name: string
	bio?: string
	image: {
		src: string
		alt?: string
		width: number
		height: number
	}
	jobTitle?: string
	companyName?: string
	socialProfiles: any
}

export const Bio = ({
	name,
	bio,
	image,
	jobTitle,
	companyName,
	socialProfiles,
}: IAuthorData) => {
	return (
		<div className={s.root}>
			<Breadcrumbs.Root className={s.breadcrumbs}>
				<Breadcrumbs.Item text={'Authors'} url="/blog" />
				<Breadcrumbs.Item text={name} url="/blog" current />
			</Breadcrumbs.Root>
			<div className={s.headshotMobile}>
				<Headshot
					image={image}
					jobTitle={jobTitle}
					companyName={companyName}
					socialProfiles={socialProfiles}
				/>
			</div>
			<div className={s.text}>
				<Text.DisplayExpressive tag={'h1'} color="strong" className={s.heading}>
					{name}
				</Text.DisplayExpressive>
				{bio ? (
					<Text.Body color="primary" className={s.bio}>
						{bio}
					</Text.Body>
				) : null}
			</div>
			<div className={s.headshot}>
				<Headshot
					image={image}
					jobTitle={jobTitle}
					companyName={companyName}
					socialProfiles={socialProfiles}
				/>
			</div>
		</div>
	)
}

export interface HeadshotProps {
	jobTitle?: string | null
	companyName?: string
	image: {
		src: string
		alt?: string | null
		width?: number | null
		height?: number | null
	}
	socialProfiles?: {
		href: string
		name: string
		icon: string
	}[]
}

const Headshot = ({
	image,
	jobTitle,
	companyName,
	socialProfiles,
}: HeadshotProps) => {
	return (
		<div>
			<div className={s.image}>
				{image ? (
					<Image
						src={image.src}
						width={image.width}
						height={image.height}
						alt={image.alt}
					/>
				) : (
					<Image
						src={'https://www.datocms-assets.com/2885/1753314445-frame.png'}
						width={200}
						height={200}
						alt={'Avatar image of blog post author'}
					/>
				)}
			</div>
			<Text.Body tag="span" size="200" color="primary">
				{jobTitle}
			</Text.Body>
			{companyName ? (
				<Text.Body
					tag="span"
					size="200"
					color="primary"
					className={s.companyName}
				>
					{`, ${companyName}`}
				</Text.Body>
			) : null}
			{socialProfiles ? (
				<div className={s.social}>
					<ul className={s.socialLinks}>
						{socialProfiles.map(({ href, name, icon }) => (
							<li key={name}>
								<Link href={href} className={s.socialLink}>
									<VisuallyHidden>{name}</VisuallyHidden>
									<FlightIcon
										name={icon}
										color="var(--mds-color-foreground-disabled)"
										size={24}
									/>
								</Link>
							</li>
						))}
					</ul>
				</div>
			) : null}
		</div>
	)
}
